import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { signOut } from '../../services/auth';
import { supabase } from '../../services/supabase'; // تأكد من المسار
import ConfirmDialog from '../common/ConfirmDialog'; // تأكد من صحة المسار للمكون بتاعك
import './TopBar.css';

function TopBar({ onMenuClick }) {
  const { landId: paramLandId, cropId: paramCropId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  // محاولة استخراج المعرفات من الرابط في حال لم يوفرها useParams
  const pathParts = location.pathname.split('/');
  const landIndex = pathParts.indexOf('land');
  const cropIndex = pathParts.indexOf('crop');
  
  const landId = paramLandId || (landIndex !== -1 ? pathParts[landIndex + 1] : null);
  const cropId = paramCropId || (cropIndex !== -1 ? pathParts[cropIndex + 1] : null);

  // الحالة الخاصة بنافذة تأكيد الخروج
  const [isLogoutDialogOpen, setIsLogoutDialogOpen] = useState(false);
  const [contextNames, setContextNames] = useState({ land: '', crop: '' });
  const getPageTitle = () => {
    const path = location.pathname;
    if (path.includes('/reports')) return 'التقارير';
    if (path.includes('/expenses')) return 'المصروفات';
    if (path.includes('/labor')) return 'إدارة العمالة';
    if (path.includes('/chemicals')) return 'إدارة الكيماويات';
    if (path.includes('/spray-cycles')) return 'دورات الرش';
    if (path.includes('/irrigation')) return 'إدارة الري';
    if (path.includes('/notifications')) return 'الإشعارات';
    if (path.includes('/income')) return 'الإيرادات';
    if (path.includes('/account')) return 'حسابي';
    if (path.includes('/settings')) return 'الإعدادات';
    if (path.includes('/machines')) return 'الآلات الزراعية';
    if (path.includes('/maintenance')) return 'صيانة المعدات';
    if (path.includes('/suggestions')) return 'الاقتراحات';
    if (path.includes('/ai-chat')) return 'دردشة AI';
    
    // Check main paths if they don't have deeper sub-routes
    if (path.includes('/crop/')) return 'الوضع المالي';
    if (path.includes('/land/')) return 'الزراعات';
    if (path.includes('/dashboard')) return 'الأراضي';
    
    return 'الوضع المالي';
  };
  useEffect(() => {
    const fetchNames = async () => {
      if (!landId || !cropId) return;

      try {
        // جلب اسم الأرض
        const { data: landData } = await supabase.from('lands').select('name').eq('id', landId).single();
        // جلب اسم الزرعة
        const { data: cropData } = await supabase.from('crops').select('crop_name').eq('id', cropId).single();

        setContextNames({
          land: landData?.name || 'أرض غير معروفة',
          crop: cropData?.crop_name || 'زرعة غير معروفة'
        });
      } catch (error) {
        console.error("Error fetching context names:", error);
      }
    };

    fetchNames();
  }, [landId, cropId]);
  // عند الضغط على زر "خروج"
  const handleLogoutClick = () => {
    setIsLogoutDialogOpen(true);
  };

  // تنفيذ تسجيل الخروج الفعلي بعد التأكيد
  const handleConfirmLogout = async () => {
    setIsLogoutDialogOpen(false);
    const result = await signOut();
    if (result.success) {
      navigate('/login', { replace: true });
    } else {
      console.error('Logout failed');
    }
  };
  // داخل مكون TopBar
  const handleAccountClick = () => {
    // إذا كنا داخل مسار يحتوي على dashboard، نوجه للحساب التابع له
    if (location.pathname.includes('/dashboard')) {
      // نأخذ المسار الحالي ونستبدل الجزء الأخير بـ account
      const baseDashPath = location.pathname.split('/').slice(0, 6).join('/');
      navigate(`${baseDashPath}/account`);
    } else {
      navigate('/account');
    }
  };
  return (
    <>
      <header className="dashboard-topbar">
        <div className="topbar-left">
          <button className="menu-btn" onClick={onMenuClick} aria-label="فتح القائمة">
            <span className="material-symbols-outlined">menu</span>
          </button>
        </div>

        <div className="topbar-right">
          <div>
            <h1 className="page-title">{getPageTitle()} | <span className="beta-label">نسخة تجريبية</span></h1>
          </div>
          {landId && cropId && (
            <div className="context-info">
              <span className="context-item">
                <span className="material-symbols-outlined context-icon">location_on</span>
                {contextNames.land}
              </span>
              <span className="context-divider">/</span>
              <span className="context-item">
                <span className="material-symbols-outlined context-icon">Agriculture</span>
                {contextNames.crop}
              </span>
            </div>
          )}
        </div>

        <div className="topbar-actions">
          {/* زر شاركنا رأيك - Feedback */}
          <a
            href="https://docs.google.com/forms/d/e/1FAIpQLSfHRdh26_WlS8-67VShpVUtKbvUtC3_sKluUdPc_tTQFsIVOA/viewform?usp=header"
            target="_blank"
            rel="noopener noreferrer"
            className="topbar-feedback-btn hide-on-mobile"
            title="شاركنا رأيك"
          >
            <span className="material-symbols-outlined">rate_review</span>
            <span className="topbar-icon-label">شاركنا رأيك</span>
          </a>

          {/* زر تسجيل الخروج - اللون الأحمر واللوجو */}
          <div
            className="topbar-icon-btn logout-btn hide-on-mobile"
            role="button"
            tabIndex={0}
            onClick={handleLogoutClick}
          >
            <span className="material-symbols-outlined">logout</span>
            <span className="topbar-icon-label">خروج</span>
          </div>

          {/* زر الإشعارات */}
          <div
            className={`topbar-icon-btn ${location.pathname.includes('notifications') ? 'active' : ''}`}
            onClick={() => navigate(`/dashboard/land/${landId}/crop/${cropId}/notifications`)}
          >
            <span className="material-symbols-outlined">notifications</span>
            <span className="notification-dot"></span>
            <span className="topbar-icon-label">الإشعارات</span>
          </div>

          {/* زر الحساب */}
          <div
            className={`topbar-user hide-on-mobile ${location.pathname.endsWith('/account') ? 'active' : ''}`}
            onClick={handleAccountClick}
            style={{ cursor: 'pointer' }}
          >
            <img
              src="https://api.dicebear.com/7.x/avataaars/svg?seed=Jade"
              alt="User"
              className="topbar-avatar"
            />
            <span className="topbar-icon-label">حسابي</span>
          </div>
        </div>
      </header>

      {/* استدعاء مكون التأكيد الخاص بك */}
      <ConfirmDialog
        open={isLogoutDialogOpen}
        title="تأكيد تسجيل الخروج"
        message="هل أنت متأكد أنك تريد الخروج من النظام؟"
        confirmLabel="تسجيل خروج"
        cancelLabel="إلغاء"
        onConfirm={handleConfirmLogout}
        onCancel={() => setIsLogoutDialogOpen(false)}
      />
    </>
  );
}

export default TopBar;